/**
 * The other door: a file she moves herself.
 *
 * Not a fallback in the apologetic sense. This is the path that works on a managed work laptop,
 * on hotel Wi-Fi with client isolation, on a network where the two devices cannot see each other,
 * and in five years when the WebRTC API has moved again. The live channel is faster and more
 * pleasant; this one is the one that always works, and the pairing screen gives them equal
 * standing for that reason.
 *
 * The file is read with FileReader and parsed in this tab. It is never uploaded, and there is no
 * code path in the portal that could upload it: the only network client the app has is the
 * PocketBase instance in `pb.ts`, and nothing here touches it.
 */

import { parsePayload, PayloadError, type LinkPayload } from './payload'
import { open } from './crypto'

/** What the phone writes. Plain JSON when unencrypted, or a sealed envelope with a passphrase. */
interface Envelope {
  covella: string
  sealed?: string
  payload?: unknown
}

export const LINK_FILE_ACCEPT = '.covella,.json,application/json'

/**
 * Reads an export off disk.
 *
 * A passphrase is only asked for when the file says it needs one, which is why the envelope
 * carries `sealed` as its own field rather than the reader guessing from whether the JSON parses.
 * Guessing produces the worst possible error message: "that file is not a Covellah export" for a
 * file that is one, encrypted.
 */
export async function readLinkFile(file: File, passphrase?: string): Promise<LinkPayload> {
  const text = await file.text()

  let envelope: Envelope
  try {
    envelope = JSON.parse(text) as Envelope
  } catch {
    throw new PayloadError(
      'That file could not be read as an export. Make sure it is the .covella file the app wrote, not a screenshot or a zip.',
    )
  }

  if (typeof envelope !== 'object' || envelope === null || typeof envelope.covella !== 'string') {
    throw new PayloadError('That file is not a Covellah export.')
  }

  if (envelope.sealed) {
    if (!passphrase) throw new NeedsPassphrase()
    let plain: string
    try {
      plain = await open(await passphraseKey(passphrase, envelope.covella), envelope.sealed)
    } catch {
      throw new PayloadError('That passphrase does not open this file. Check it and try again.')
    }
    return parsePayload(JSON.parse(plain))
  }

  return parsePayload(envelope.payload)
}

/** Thrown rather than returned, so the caller cannot forget to handle it and render a blank page. */
export class NeedsPassphrase extends Error {
  constructor() {
    super('This export is protected by a passphrase.')
  }
}

/**
 * The key an exported file is sealed with.
 *
 * PBKDF2 with a high iteration count because the input is a passphrase a person chose, and the
 * file may sit in a downloads folder for months. The salt is the export's own id, carried in the
 * clear in the envelope, so two exports with the same passphrase still seal to different keys.
 */
async function passphraseKey(passphrase: string, salt: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new TextEncoder().encode(salt),
      iterations: 310_000,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  )
}
